import { useMutation, useQueryClient } from '@tanstack/react-query';
import { RefreshCw, TriangleAlert } from 'lucide-react';
import { FormattedMessage } from 'react-intl';

import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { type Project } from '@/lib/queries/project';
import {
  refreshStateAndCloud,
  regenerateTimeMachine,
} from '@/lib/queries/refreshGraph';
import { cn } from '@/lib/utils';

interface Props {
  mainProject: Project;
}

export const RegenerateGraph = ({ mainProject }: Props): JSX.Element => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { isPending, mutateAsync } = useMutation({
    mutationKey: ['project', mainProject.id, 'regenerate-graph'],
    mutationFn: async () => {
      await refreshStateAndCloud(mainProject.id);
    },
    onSuccess: async () => {
      toast({
        title: 'configuration.regenerateGraph.button.success',
        variant: 'default',
      });
      queryClient.invalidateQueries();
    },
    onError: () => {
      toast({
        title: 'configuration.regenerateGraph.button.error',
        variant: 'destructive',
      });
    },
  });

  const {
    isPending: isTimeMachinePending,
    mutateAsync: mutateTimeMachineAsync,
  } = useMutation({
    mutationKey: ['project', mainProject.id, 'regenerate-time-machine'],
    mutationFn: async () => {
      await regenerateTimeMachine(mainProject.id);
    },
    onSuccess: async () => {
      toast({
        title: 'configuration.regenerateGraph.timeMachine.button.success',
        variant: 'default',
      });
      queryClient.invalidateQueries();
    },
    onError: () => {
      toast({
        title: 'configuration.regenerateGraph.timeMachine.button.error',
        variant: 'destructive',
      });
    },
  });

  return (
    <>
      <Button
        variant="destructive"
        className="text-white"
        onClick={() => mutateAsync()}
        disabled={isPending}
      >
        <RefreshCw
          size={24}
          className={cn('inline mr-2', isPending ? 'animate-spin' : '')}
        />
        {isPending ? (
          <FormattedMessage id="configuration.regenerateGraph.button.pending" />
        ) : (
          <FormattedMessage id="configuration.regenerateGraph.button.label" />
        )}
      </Button>
      <Button
        variant="destructive"
        className="text-white ml-2"
        onClick={() => mutateTimeMachineAsync()}
        disabled={isTimeMachinePending}
      >
        <TriangleAlert
          size={24}
          className={cn(
            'inline mr-2',
            isTimeMachinePending ? 'animate-spin' : '',
          )}
        />
        {isTimeMachinePending ? (
          <FormattedMessage id="configuration.regenerateGraph.timeMachine.button.pending" />
        ) : (
          <FormattedMessage id="configuration.regenerateGraph.timeMachine.button.label" />
        )}
      </Button>
      {isTimeMachinePending && (
        <p className="text-xs text-red-500">
          This might take a (very) long while, go grab a drink
        </p>
      )}
    </>
  );
};
